// Default landing view for the live preview: a table of saved records
// with Add/Edit/Delete actions that always work (even when the
// metadata declares no actions).
//
// The grid is augmented with the shared embed-grid UX features:
// search, per-column filter, multi-column sort, full-screen toggle,
// and (when provided via columnTotals) a footer totals row.
//
// ── why every row lookup goes through columnFormKey ──────────────────────
// The engine's list SELECT aliases each projected column to its FORM KEY
// (executor._build_select_list / _column_form_key), not to its db_name —
// because a form that joins a master can hold two columns with the same
// db_name (RENEWER_NAME and APPROVER_NAME both read EMPLOYEE_NAME off two
// aliased joins of the employee master), and keying by db_name collapses
// them onto one slot. So a row arrives as { renewer_name, approver_name },
// and a cell that asked for row['employee_name'] rendered blank while the
// record popup — which reads form state by form key — showed the value.
// Search / filter / sort / totals run in-memory over these same rows, so
// they take the form key too.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDownAZ, ArrowUpAZ, Eye, History, Mail, Maximize2, Minimize2, Pencil, Play, Plus, RefreshCcw, Tags, Trash2, UserPlus, Waypoints, X } from 'lucide-react'
import RowActionsMenu from './RowActionsMenu'
import EnforcedFilterControls, { type OverrideMap } from './EnforcedFilterControls'
import type { ActionDef, ColumnDef, SqlModel } from '../types'
import type {
  EnforcedConditionEcho, EnforcedFilterSetEcho, FilterNotice,
} from '../contracts'
import {
  columnDbName, columnFormKey, ddlbDisplayLabel, listOrderedColumns,
  hasImageUpload, hasDocumentUpload, hasUrlWidget,
  richCellText, listHeadingOf,
} from '../utils'
import { DisplayCell, displayPlainText, hasDisplayFormat, temporalCellText } from '../displayFormat'
import { personLabel, usePeople, type PeopleDirectory } from '../people'
import { openRecord, rowEditable, rowHiddenActions, rowLockReason, visibleRowActions } from '../utils'
import { useKeyboardShortcuts } from '../keyboard'
import SplitScrollTable, { type SplitScrollHandle } from '../SplitScrollTable'
import { displayNameForUrl } from '../widgets/FileUploadField'
import type {
  EmbedColumnTotal, EmbedGridUx,
} from '@twasta/page-schema'
import GridToolbar from './GridToolbar'
import FilterChipBar from './FilterChipBar'
import AddFilterButton from './AddFilterButton'
import ColumnTotalsRow from './ColumnTotalsRow'
import { applyGridState } from './applyGridState'
import { useGridState } from './useGridState'
import {
  colKeyOf, orderedDisplayCols, widthOf, useColumnDnd, useResolvedGridLayout,
  RESIZE_HANDLE_STYLE, type GridLayoutApi,
} from './columnLayout'
import { LayoutActions } from './LayoutActions'
import PaginationBar, { type ListPagination } from './PaginationBar'
import { tw } from '../../testHooks'
import { useObjectChrome } from '../../objectChrome'
import { useTxnCustomize } from '../../customize/txnContext'
import { colPath as czColPath, formPath as czFormPath, setLeaf as czSetLeaf } from '../../customize/txnOps'
import { CustomizedDot, GearButton, HiddenTray } from '../../customize/ui'
import { isListHidden } from '../utils'

interface RowShape extends Record<string, unknown> {
  _txn_id: string
}

interface Props {
  model: SqlModel
  rows: RowShape[]
  total: number
  busy: boolean
  /** Draw the object's name at the head of the toolbar. Defaults TRUE — on a
   *  surface with no chrome of its own (the designer's Live Preview) this is
   *  the only thing naming the object. The host passes false when the
   *  surrounding shell already names it; the record count and every control
   *  stay either way. See ../../objectChrome. */
  showTitle?: boolean
  // Live column layout (drag to resize / reorder). When a host supplies a
  // `gridLayout` + this list's `formNo`, the change can be applied back to the
  // design (author only); otherwise it's a local, per-session view tweak.
  gridLayout?: GridLayoutApi
  formNo?: string
  selectedTxnId?: string | null
  // ── End-user rights gating (standalone runtime) ─────────────────────────
  // Both default TRUE so existing hosts (designer preview / platform Run) are
  // unchanged. When false the corresponding affordance is hidden entirely —
  // this is UX on top of the server-side authorization, not the gate itself.
  /** Show the Add button (+ its F3 shortcut). */
  canAdd?: boolean
  /** Show the per-row Delete action (+ its Ctrl+Delete shortcut). */
  canDelete?: boolean
  onAdd: () => void
  onEdit: (txnId: string) => void
  // When set, clicking (or double-clicking) a row opens it READ-ONLY through
  // this instead of for editing — Vision's browse screen, where only the Edit
  // control (pencil / F4 / ⋮ Edit) opens a record in edit mode.
  onView?: (txnId: string) => void
  onDelete: (txnId: string) => void
  // When set, a History action appears per row (opens the record's audit
  // timeline). Omitted on surfaces that don't surface history.
  onHistory?: (txnId: string) => void
  // When set, a "Messages" action appears per row (the record's message log —
  // only for an object that sends messages).
  onMessages?: (txnId: string) => void
  // When set, an "Also known as" action appears per row. Present only for a
  // vector-searchable MASTER whose model allows aliases — teaching an alias is
  // meaningless on an object nothing searches semantically.
  onAlias?: (txnId: string) => void
  // When set, an "Assign to…" action appears per row: hand THIS record to a
  // person or a role, so it lands in their work list opened on the record.
  // The human-authored twin of a work rule, and the reason a colleague can be
  // given something no rule anticipated. Absent on hosts with no work panel.
  onAssign?: (txnId: string) => void
  // When set, a "Process trail" action appears per row: this record's chain
  // across the objects it is linked to. A trail is about ONE record, so it
  // lives here and never on the toolbar, where nothing is selected yet. Set
  // only when the object is actually placed on a process flow.
  onProcessTrail?: (txnId: string) => void
  onRefresh: () => void
  // ── Custom action buttons (scope-aware placement) ───────────────────────
  // Form-scoped actions are rendered as buttons just before "Add"; row-scoped
  // actions are added to each row's 3-dot (⋮) menu and run against that row.
  // The host owns execution (running the engine action + applying the result).
  formActions?: ActionDef[]
  rowActions?: ActionDef[]
  onRunFormAction?: (name: string) => void
  onRunRowAction?: (name: string, txnId: string) => void
  // ── Bulk actions (multi-row select) ─────────────────────────────────────
  // When `bulkActions` is non-empty and `onRunBulkAction` is wired, a leading
  // checkbox column + a selection bar appear: the user ticks rows and runs one
  // of these actions against the whole selection at once. The host owns
  // execution (the one-time confirmation gate + the results modal). Pass the
  // bulk-eligible subset of the row actions (see bulkEligibleActions).
  bulkActions?: ActionDef[]
  onRunBulkAction?: (name: string, txnIds: string[]) => void
  bulkBusy?: boolean
  // Select-all-across-pages: when supplied, the selection bar offers a "Select
  // all N" link (once every loaded row is ticked and more match on the server)
  // that resolves EVERY matching record's txn_id under the current server-side
  // filters. Returns the ids (the host toasts if the set was capped). Omitted ⇒
  // selection is limited to the loaded rows.
  onSelectAllMatching?: () => Promise<string[]>
  // System add / edit actions merge into the built-in controls instead of
  // rendering as extra buttons: ``add`` renames the "Add" button, ``edit``
  // becomes the row pencil's hover text. Clicking the built-in then routes
  // through onRunFormAction / onRunRowAction so the action's pre-action
  // validations run before the operation (see SystemActionOverrides).
  systemOverrides?: { add?: ActionDef; edit?: ActionDef; view?: ActionDef; delete?: ActionDef }
  /**
   * When set, single row click invokes onSelect (typically: bind
   * selection variables) and double-click invokes onEdit. When omitted,
   * single click falls back to onEdit so existing usages — e.g. the
   * standalone Live Preview tab — keep their original behaviour.
   * Smart-page embed previews supply this so a row click is just a
   * selection and the editor opens only on explicit double-click.
   */
  onSelect?: (row: RowShape) => void
  // When false, the list's keyboard shortcuts (F3 add / F4 edit / Ctrl+Delete
  // / Ctrl+N · Ctrl+P paging / Ctrl+F search) are suspended — hosts pass
  // `!editorOpen` so the record editor's own bindings (Ctrl+S, F2, …) take
  // over while a record is open. Default on.
  shortcutsEnabled?: boolean
  // Extra controls rendered in the header action group, after "Add".
  // The Live Preview tab uses this to host its deploy menu; embed
  // previews leave it unset.
  headerActions?: React.ReactNode
  // Controls rendered just after "Refresh" and before "Add" — the natural
  // home for the Approvals inbox button so it reads next to the primary list
  // actions rather than trailing the overflow menu.
  beforeAddActions?: React.ReactNode
  // Optional control rendered on the right of the search/filter toolbar
  // row (GridToolbar.rightSlot). Smart-page embeds use this to host the
  // runtime interface switcher (List / Multi-tabular / Form-input).
  viewSwitcher?: React.ReactNode
  // Imperative control of this list's split-scroll table (e.g. a "Freeze
  // first column" menu item in the host's header overflow menu).
  splitHandle?: React.Ref<SplitScrollHandle>
  // Grid UX configuration. Omitted = everything default-on.
  gridUx?: EmbedGridUx
  // Per-column totals shown in the footer.
  columnTotals?: Record<string, EmbedColumnTotal>
  // Designer-enforced conditions currently in effect (resolved values from
  // the server). When set, a form-like filter bar is shown above the grid:
  // the end user can change each condition's value (unless `locked`) but
  // cannot remove it.
  enforcedFilters?: EnforcedConditionEcho[]
  onApplyFilters?: (overrides: OverrideMap) => void
  // Non-blocking messages ('W'/'P') from the conditions' own validations.
  filterNotices?: FilterNotice[]
  // Project folder + object name. Only the enforced-filter bar needs them,
  // and only to open a condition's reference picker.
  project?: string
  objName?: string
  // Designer-defined filter sets the end user can switch between, plus a
  // built-in "No filter (all records)" bypass. When set (and onSelectFilterSet
  // is provided) a set picker is shown above the condition inputs.
  filterSets?: EnforcedFilterSetEcho[]
  activeFilterSetId?: string | null
  bypassed?: boolean
  onSelectFilterSet?: (sel: { setId?: string; bypass?: boolean }) => void
  // Server-side pagination. When only the infinite fields are supplied, a
  // "Load earlier entries" button appends the next (older) page. When the
  // optional dual-mode fields are also supplied, the footer additionally
  // offers a page-size selector and an Infinite ⇆ Paged toggle; in Paged mode
  // Prev/Next replace the visible rows with exactly one server page (keeping
  // the DOM bounded). Sort/search remain client-side over the loaded rows.
  // The bar itself is <PaginationBar>, shared with every other view of the
  // same records — see that module.
  pagination?: ListPagination
  // An extra read-only column just before the row actions, drawn per row by
  // the host — the additional attributes' Flags (flag icons a rule raises).
  // Absent ⇒ no column.
  rowAdornment?: (row: RowShape) => React.ReactNode
  rowAdornmentHeading?: string
  // The full screen the expand button drives, when the host owns one for a
  // surface larger than this list (Customize mode: its bar and field panel go
  // full screen WITH the list). Absent ⇒ the shell's, else the list's own.
  fullScreen?: { on: boolean; set: (on: boolean) => void }
}

/** Width of the host-drawn adornment column (`rowAdornment`). */
const ADORNMENT_COL_WIDTH = 96

export default function ListView({
  model, rows, total, busy, showTitle = true, gridLayout, formNo, selectedTxnId,
  canAdd = true, canDelete = true,
  onAdd, onEdit, onView, onDelete, onHistory, onMessages, onAlias, onAssign, onProcessTrail, onRefresh, onSelect,
  formActions, rowActions, onRunFormAction, onRunRowAction, systemOverrides,
  bulkActions, onRunBulkAction, bulkBusy, onSelectAllMatching,
  headerActions, beforeAddActions, viewSwitcher, splitHandle, gridUx, columnTotals, enforcedFilters, onApplyFilters,
  filterSets, activeFilterSetId, bypassed, onSelectFilterSet, pagination,
  filterNotices, project, objName,
  shortcutsEnabled, rowAdornment, rowAdornmentHeading, fullScreen,
}: Props) {
  // Who the rows' user-key columns point at, named by the engine — so a column
  // headed "Asked by" reads as a person rather than `443a07c7-526c-…`.
  const people = usePeople()
  // Show every visible column from the metadata, ordered by the designer's
  // left-to-right grid position (x), with any live width/order override applied
  // on top (drag to resize / reorder — see useResolvedGridLayout).
  const baseCols = useMemo(() => listOrderedColumns(model), [model])
  // Customize mode: ⚙ on every header, the freeze saved for the organization,
  // and the columns not in the list in a tray under the grid.
  const cz = useTxnCustomize()
  const czForm = cz ? (formNo || model.form_no || '1') : null
  const rl = useResolvedGridLayout(gridLayout, formNo)
  const cols = useMemo(() => orderedDisplayCols(baseCols, rl.layout), [baseCols, rl.layout])
  const wOf = useCallback((c: ColumnDef) => widthOf(c, rl.layout), [rl.layout])

  // System add / edit overrides: relabel the built-in Add button / retitle
  // the row pencil and route the click through the action runner so the
  // action's pre-action validations run first. Without a runner (host didn't
  // wire actions) the plain built-in behaviour is kept.
  const addOverride = onRunFormAction ? systemOverrides?.add : undefined
  const editOverride = onRunRowAction ? systemOverrides?.edit : undefined
  const viewOverride = onRunRowAction ? systemOverrides?.view : undefined
  const addLabel = addOverride?.name || 'Add'
  // Every way of opening a record (pencil/eye, row click, double-click, F4)
  // goes through here. An EDITABLE row routes through the edit system action
  // (its conditions gate opening for edit); a NON-editable row routes through
  // the VIEW system action (its conditions gate opening read-only) — the edit
  // action's conditions must NOT run when the record is only being viewed.
  const openEdit = useCallback((txnId: string) => {
    const row = rows.find(r => r._txn_id === txnId)
    openRecord({
      txnId,
      editable: !row || rowEditable(row),
      systemOverrides: { edit: editOverride, view: viewOverride },
      onRunRowAction,
      onOpen: (id) => onEdit(id),
    })
  }, [rows, editOverride, viewOverride, onRunRowAction, onEdit])
  // A row click: read-only when the host asked for it (onView), through the
  // view system action's gate like any other read-only open.
  const openRow = useCallback((txnId: string) => {
    if (!onView) { openEdit(txnId); return }
    openRecord({
      txnId,
      editable: false,
      systemOverrides: { view: viewOverride },
      onRunRowAction,
      onOpen: (id) => onView(id),
    })
  }, [onView, openEdit, viewOverride, onRunRowAction])
  const dnd = useColumnDnd({
    displayKeys: useMemo(() => cols.map(colKeyOf), [cols]),
    enabled: true,
    onReorder: rl.setOrder,
    onResize: rl.setWidth,
  })
  const ux: EmbedGridUx = gridUx || {}
  const grid = useGridState()
  // Inside the Run shell the expand button is the SHELL's full screen (all
  // chrome hidden, the screen fills the window); elsewhere the list overlays
  // itself as before.
  const chrome = useObjectChrome()
  const shellFs = fullScreen
    ?? (chrome?.fullScreen && objName && chrome.objName === objName ? chrome.fullScreen : null)
  const overlayFull = shellFs ? false : grid.isFullScreen
  const fullOn = shellFs ? shellFs.on : grid.isFullScreen
  const toggleFull = shellFs ? () => shellFs.set(!shellFs.on) : grid.toggleFullScreen

  // Apply search / filter / sort to the rows in-memory.
  const visibleRows = useMemo(() => {
    if (!grid.search && grid.filters.length === 0 && grid.sort.length === 0) {
      return rows
    }
    return applyGridState(rows, grid.search, grid.filters, grid.sort)
  }, [rows, grid.search, grid.filters, grid.sort])

  // ── Bulk multi-row selection ────────────────────────────────────────────
  // Available only when the host wired bulk actions. Selection is held by
  // _txn_id and pruned whenever the underlying rows change (e.g. after a bulk
  // run refreshes the list) so it never references gone records.
  const bulkEnabled = (bulkActions?.length || 0) > 0 && !!onRunBulkAction
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  // Anchor row for shift-click range selection (the last checkbox clicked
  // without Shift). Held by _txn_id so it survives reorders/filtering.
  const [anchorId, setAnchorId] = useState<string | null>(null)
  const [selectingAll, setSelectingAll] = useState(false)
  useEffect(() => {
    if (!bulkEnabled) return
    setSelectedIds(prev => {
      if (prev.size === 0) return prev
      const present = new Set(rows.map(r => r._txn_id))
      let changed = false
      const next = new Set<string>()
      prev.forEach(id => { if (present.has(id)) next.add(id); else changed = true })
      return changed ? next : prev
    })
  }, [rows, bulkEnabled])
  const toggleRowSelected = useCallback((txnId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(txnId)) next.delete(txnId)
      else next.add(txnId)
      return next
    })
  }, [])
  const clearSelection = useCallback(() => { setSelectedIds(new Set()); setAnchorId(null) }, [])

  // ── Keyboard shortcuts ─────────────────────────────────────────────────────
  // F3 add · F4 edit · Ctrl+Delete delete (all on the keyboard-target row) ·
  // Ctrl+N / Ctrl+P page · Ctrl+F focus search. The target row is the one the
  // user last hovered/clicked, falling back to the host selection, then the
  // first visible row. Scoped to the list container so only the hovered/
  // focused list responds when several are on one page.
  const listRef = useRef<HTMLDivElement>(null)
  const [activeTxnId, setActiveTxnId] = useState<string | null>(null)
  const kbTargetId = activeTxnId ?? selectedTxnId ?? (visibleRows[0]?._txn_id ?? null)
  const doAdd = useCallback(() => {
    if (busy || !canAdd) return
    if (addOverride) onRunFormAction!(addOverride.name)
    else onAdd()
  }, [busy, canAdd, addOverride, onRunFormAction, onAdd])
  const focusSearch = useCallback(() => {
    listRef.current?.querySelector<HTMLInputElement>('input[data-grid-search]')?.focus()
  }, [])
  const pageStep = useCallback((dir: 1 | -1) => {
    const p = pagination
    if (!p) return
    if (p.mode === 'paged' && p.onGotoPage) {
      const page = p.page || 0
      const pageCount = Math.max(1, Math.ceil((p.total || 0) / Math.max(1, p.pageSize || 50)))
      const next = page + dir
      if (next >= 0 && next <= pageCount - 1) p.onGotoPage(next)
    } else if (dir === 1 && p.hasMore) {
      // Continuous mode has no "previous" — Ctrl+N appends the next (older) page.
      p.onLoadEarlier()
    }
  }, [pagination])
  useKeyboardShortcuts(
    [
      { combo: 'f3', run: doAdd },
      { combo: 'f4', run: () => { if (kbTargetId) openEdit(kbTargetId) } },
      { combo: 'ctrl+delete', run: () => { if (kbTargetId && canDelete) onDelete(kbTargetId) } },
      { combo: 'ctrl+n', run: () => pageStep(1) },
      { combo: 'ctrl+p', run: () => pageStep(-1) },
      { combo: 'ctrl+f', run: focusSearch },
    ],
    { enabled: shortcutsEnabled !== false, scopeRef: listRef },
  )

  const sortIndex = useMemo(() => {
    const m = new Map<string, { dir: 'asc' | 'desc'; idx: number }>()
    grid.sort.forEach((s, i) => m.set(s.column, { dir: s.dir, idx: i }))
    return m
  }, [grid.sort])

  // A total is authored against the column's db_name (the page designer's
  // key), while the footer now reads rows — and so looks its spec up — by
  // form key. Re-key once so a total configured on a column whose name and
  // db_name differ keeps its footer instead of quietly disappearing.
  const totalsByFormKey = useMemo(() => {
    if (!columnTotals) return undefined
    const out: Record<string, EmbedColumnTotal> = {}
    for (const c of cols) {
      const fk = columnFormKey(c)
      const spec = columnTotals[fk] ?? columnTotals[columnDbName(c)]
      if (spec) out[fk] = spec
    }
    return out
  }, [columnTotals, cols])

  const showTotals = ux.totals !== false && totalsByFormKey
    && Object.values(totalsByFormKey).some(Boolean)

  // Bulk selection derived from the currently visible rows.
  const selectedCount = selectedIds.size
  const allVisibleSelected = bulkEnabled && visibleRows.length > 0
    && visibleRows.every(r => selectedIds.has(r._txn_id))
  const toggleSelectAll = useCallback(() => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      const everySelected = visibleRows.length > 0
        && visibleRows.every(r => next.has(r._txn_id))
      if (everySelected) visibleRows.forEach(r => next.delete(r._txn_id))
      else visibleRows.forEach(r => next.add(r._txn_id))
      return next
    })
  }, [visibleRows])
  // A row checkbox click. Shift+click extends the selection from the anchor
  // (the last plain-clicked row) to here; a plain click toggles + re-anchors.
  const handleRowCheckboxClick = useCallback((e: React.MouseEvent, txnId: string) => {
    e.stopPropagation()
    if (e.shiftKey && anchorId) {
      const a = visibleRows.findIndex(r => r._txn_id === anchorId)
      const b = visibleRows.findIndex(r => r._txn_id === txnId)
      if (a !== -1 && b !== -1) {
        const [lo, hi] = a <= b ? [a, b] : [b, a]
        setSelectedIds(prev => {
          const next = new Set(prev)
          for (let k = lo; k <= hi; k++) next.add(visibleRows[k]._txn_id)
          return next
        })
        setAnchorId(txnId)
        return
      }
    }
    toggleRowSelected(txnId)
    setAnchorId(txnId)
  }, [anchorId, visibleRows, toggleRowSelected])
  // The select-all-across-pages link is offered only when no client-side
  // search/column-filter is narrowing the loaded rows (the server can't honour
  // an in-memory filter), every loaded row is ticked, and more rows match on
  // the server than are loaded.
  const clientFilterActive = !!grid.search || grid.filters.length > 0
  const moreOnServer = bulkEnabled && !!onSelectAllMatching && !clientFilterActive
    && allVisibleSelected && total > visibleRows.length
  const doSelectAllMatching = useCallback(async () => {
    if (!onSelectAllMatching) return
    setSelectingAll(true)
    try {
      setSelectedIds(new Set(await onSelectAllMatching()))
    } finally {
      setSelectingAll(false)
    }
  }, [onSelectAllMatching])

  const containerStyle: React.CSSProperties = overlayFull ? {
    // Above the Run view's sticky top bar (zIndex 9000) so the fullscreen
    // grid isn't truncated behind it; preserves order under the editor family.
    position: 'fixed', inset: 0, zIndex: 9410,
    background: 'var(--color-bg)',
    display: 'flex', flexDirection: 'column',
  } : {
    // Fill the parent as a flex child rather than `height: 100%`. `height:100%`
    // needs a parent with a DEFINITE height; an embed box that only sets
    // `maxHeight` (to grow with data up to a cap) is indefinite, so `height:100%`
    // would collapse to content and the inner grid wouldn't scroll. `flex:1 +
    // minHeight:0` respects a flex parent's max-height clamp, so the grid scrolls
    // inside while the toolbar/header stay put. Both consumers (LivePreviewTab,
    // EmbedLivePreview) wrap ListView in a flex column.
    display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
  }

  return (
    <div ref={listRef} style={containerStyle}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 16px', borderBottom: '1px solid var(--color-border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {showTitle && (
            <strong style={{ fontSize: '0.9125rem' }}>
              {model.form_title || model.main_table}
            </strong>
          )}
          <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
            {visibleRows.length === total
              ? `${total} record${total === 1 ? '' : 's'}`
              : `${visibleRows.length} of ${total}`}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <LayoutActions
            show={!!rl.apply && rl.dirty}
            applying={rl.applying}
            onReset={rl.reset}
            onApply={rl.apply ?? (() => {})}
            applyLabel={rl.applyLabel}
            applyTitle={rl.applyTitle}
          />
          <button className="btn btn-ghost btn-sm" onClick={onRefresh} disabled={busy} title="Refresh"
                  {...tw('action', 'refresh')}>
            <RefreshCcw size={14} />
          </button>
          {/* Secondary, list-wide affordances (e.g. the Approvals inbox) sit
              between Refresh and the primary Add button. */}
          {beforeAddActions}
          {/* Form-scoped custom actions (generic — not tied to a row) render
              just before Add, per the action's resolved scope. */}
          {(formActions || []).map((a) => (
            <button
              key={`fa-${a.name}`}
              className="btn btn-ghost btn-sm"
              {...tw('action', a.name, { scope: 'form' })}
              onClick={() => onRunFormAction?.(a.name)}
              disabled={busy}
              title={a.name}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <Play size={13} /> {a.name}
            </button>
          ))}
          {/* Divider sets the primary action apart from the secondary group. */}
          <span style={toolbarDivider} aria-hidden />
          {canAdd && (
            <button
              className="btn btn-primary btn-sm"
              /* `doAdd` (never `onAdd` directly): `onAdd` is TxnScreen's `startAdd`,
                 whose parameter is an OPTIONAL seed object. TypeScript accepts that
                 for an `onAdd: () => void` prop, so binding it straight to onClick
                 type-checks while React hands it the MouseEvent as the seed — the
                 event's own keys then land in the form values (mapSeedToFormKeys
                 deliberately keeps unmapped keys) and the save body fails to
                 serialize on `target`'s React fiber cycle. doAdd takes no
                 parameters and already carries this same addOverride branch. */
              onClick={doAdd}
              disabled={busy}
              title={addOverride?.name}
              {...tw('action', 'add')}
            >
              <Plus size={14} /> {addLabel}
            </button>
          )}
          {/* Full-screen toggle — between Add and the overflow (deploy) menu. */}
          <button
            className="btn btn-ghost btn-sm"
            {...tw('action', 'fullscreen')}
            onClick={toggleFull}
            aria-pressed={fullOn}
            title={fullOn ? 'Exit full screen' : 'Full screen'}
          >
            {fullOn ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
          {headerActions}
        </div>
      </div>

      <EnforcedFilterControls
        model={model}
        enforcedFilters={enforcedFilters}
        filterSets={filterSets}
        activeFilterSetId={activeFilterSetId}
        bypassed={bypassed}
        busy={busy}
        notices={filterNotices}
        onApplyFilters={onApplyFilters}
        onSelectFilterSet={onSelectFilterSet}
        project={project}
        objName={objName}
      />

      <GridToolbar
        // The full-screen toggle now lives in the header action group (next
        // to Add), so suppress GridToolbar's own to avoid a duplicate.
        ux={{ ...ux, fullScreen: false }}
        search={grid.search}
        onSearchChange={grid.setSearch}
        sort={grid.sort}
        onClearSort={grid.clearSort}
        isFullScreen={grid.isFullScreen}
        onToggleFullScreen={grid.toggleFullScreen}
        addFilterButton={ux.filter !== false && (
          <AddFilterButton
            columns={cols}
            columnKeyOf={columnFormKey}
            filters={grid.filters}
            onUpsert={grid.upsertFilter}
            onRemove={grid.removeFilter}
          />
        )}
        rightSlot={viewSwitcher}
      />
      {ux.filter !== false && (
        <FilterChipBar
          filters={grid.filters}
          columns={cols}
          columnKeyOf={columnFormKey}
          onRemove={grid.removeFilter}
          onClearAll={grid.clearFilters}
        />
      )}

      {bulkEnabled && selectedCount > 0 && (
        <div style={selectionBarStyle}>
          <span style={{ fontSize: '0.9375rem', whiteSpace: 'nowrap' }}>
            <strong>{selectedCount}</strong> selected
          </span>
          {moreOnServer && (
            <button
              className="btn btn-link btn-sm"
              onClick={doSelectAllMatching}
              disabled={selectingAll || bulkBusy}
              title={`Select all ${total} records matching the current filters`}
              style={{
                background: 'none', border: 'none', padding: 0,
                color: 'var(--color-accent)', cursor: 'pointer',
                fontSize: '0.9125rem', whiteSpace: 'nowrap', textDecoration: 'underline',
              }}
            >
              {selectingAll ? 'Selecting…' : `Select all ${total}`}
            </button>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1 }}>
            {(bulkActions || []).map(a => (
              <button
                key={`bulk-${a.name}`}
                {...tw('action', a.name, { scope: 'bulk' })}
                className="btn btn-primary btn-sm"
                disabled={bulkBusy}
                onClick={() => onRunBulkAction!(a.name, Array.from(selectedIds))}
                title={`Run "${a.name}" on the ${selectedCount} selected record${selectedCount === 1 ? '' : 's'}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: bulkBusy ? 'wait' : 'pointer' }}
              >
                <Play size={13} /> {a.name}
              </button>
            ))}
          </div>
          <button
            className="btn btn-ghost btn-sm"
            onClick={clearSelection}
            disabled={bulkBusy}
            {...tw('action', 'clear_selection')}
            title="Clear selection"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <X size={13} /> Clear
          </button>
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {visibleRows.length === 0 ? (
          <div style={{ flex: 1, overflow: 'auto' }}>
            <div style={{
              padding: 40, textAlign: 'center',
              color: 'var(--color-text-secondary)', fontSize: '0.9625rem',
            }}>
              {rows.length === 0
                ? <>No records yet. Click <strong>{addLabel}</strong> to create one — the preview database stores it across sessions until you reset.</>
                : 'No records match the current filters / search.'}
            </div>
          </div>
        ) : (
          <SplitScrollTable
            ref={splitHandle}
            // While customizing, show (and save) the organization's freeze,
            // not this person's own.
            storageKey={czForm ? undefined : `twasta:split:list:${cols[0]?.table_name || model.form_no || 'main'}`}
            dataCount={cols.length}
            defaultFrozen={Number(model.frozen_columns) || 0}
            onSplitChange={cz && czForm ? (n) => {
              if (n === (Number(model.frozen_columns) || 0)) return
              void cz.session.apply(czSetLeaf(czFormPath(czForm, 'frozen_columns'), n),
                n ? `Freeze ${n} column${n === 1 ? '' : 's'}` : 'Unfreeze')
            } : undefined}
          >
            {(range) => {
              const paneCols = cols.slice(range.colStart, range.colEnd)
              return (
              <table style={{
                borderCollapse: 'collapse', fontSize: '0.9375rem',
                // White-in-light / dark-in-dark data surface (theme-adaptive); the
                // transparent rows show this through, so hover/selection still work.
                background: 'var(--lp-input-bg, var(--color-bg))',
                // ``fixed`` layout makes the browser honour the explicit
                // <colgroup> widths instead of distributing based on cell
                // content — pixel-accurate match with the designer.
                tableLayout: 'fixed',
                width: paneCols.reduce((sum, c) => sum + wOf(c),
                  (range.isTrailing ? ACTIONS_COL_WIDTH : 0)
                    + (range.isTrailing && rowAdornment ? ADORNMENT_COL_WIDTH : 0)
                    + (range.isLeading && bulkEnabled ? SELECT_COL_WIDTH : 0)),
              }}>
                <colgroup>
                  {range.isLeading && bulkEnabled && <col style={{ width: SELECT_COL_WIDTH }} />}
                  {paneCols.map(c => (
                    <col
                      key={c.col_id || c.name}
                      data-col-key={colKeyOf(c)}
                      style={{ width: wOf(c) }}
                    />
                  ))}
                  {range.isTrailing && rowAdornment && <col style={{ width: ADORNMENT_COL_WIDTH }} />}
                  {range.isTrailing && <col style={{ width: ACTIONS_COL_WIDTH }} />}
                </colgroup>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--color-surface-2, var(--color-bg-secondary))', zIndex: 1 }}>
              <tr>
                {range.isLeading && bulkEnabled && (
                  <th style={{ ...th, textAlign: 'center', cursor: 'default' }}>
                    <input
                      type="checkbox"
                      aria-label="Select all rows"
                      checked={allVisibleSelected}
                      ref={el => { if (el) el.indeterminate = !allVisibleSelected && selectedCount > 0 }}
                      onChange={toggleSelectAll}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                )}
                {paneCols.map((c, i) => {
                  const key = columnFormKey(c)
                  const ck = colKeyOf(c)
                  const ss = sortIndex.get(key)
                  const sortable = ux.sort !== false
                  const ind = dnd.dropIndicator?.key === ck ? dnd.dropIndicator.side : null
                  return (
                    <th
                      key={c.col_id || c.name}
                      data-split-col={range.colStart + i}
                      {...dnd.getHeaderProps(ck)}
                      style={{
                        ...th,
                        position: 'relative',
                        cursor: 'grab',
                        userSelect: 'none',
                        boxShadow: ind === 'left'
                          ? 'inset 3px 0 0 var(--color-accent)'
                          : ind === 'right'
                            ? 'inset -3px 0 0 var(--color-accent)'
                            : undefined,
                      }}
                      onClick={sortable ? (e) => grid.toggleSort(key, e.shiftKey) : undefined}
                      title={sortable
                        ? 'Drag to reorder · drag the right edge to resize · click to sort (shift-click adds a column)'
                        : 'Drag to reorder · drag the right edge to resize'}
                    >
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}>
                        {listHeadingOf(c)}
                        {cz && czForm && cz.session.countCustomized(czColPath(czForm, ck, 'x').slice(0, -2)) > 0 && <CustomizedDot />}
                        {ss && (
                          <span style={{
                            color: 'var(--color-accent, #6366f1)',
                            fontWeight: 700,
                            display: 'inline-flex', alignItems: 'center',
                          }}>
                            {ss.dir === 'asc' ? <ArrowUpAZ size={11} /> : <ArrowDownAZ size={11} />}
                            {grid.sort.length > 1 && (
                              <sup style={{ marginLeft: 2 }}>{ss.idx + 1}</sup>
                            )}
                          </span>
                        )}
                      </span>
                      {cz && czForm && (
                        <GearButton label={`Change ${listHeadingOf(c)}`}
                          active={cz.openFieldKey === `${czForm}|${ck}`}
                          onClick={(el) => cz.openField(czForm, ck, el)}
                          style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }} />
                      )}
                      <div {...dnd.getResizeHandleProps(ck)} style={RESIZE_HANDLE_STYLE} />
                    </th>
                  )
                })}
                {range.isTrailing && rowAdornment && (
                  <th style={{ ...th, cursor: 'default' }}>{rowAdornmentHeading || ''}</th>
                )}
                {range.isTrailing && (
                  <th style={{
                    ...th, textAlign: 'right',
                    position: 'sticky', right: 0,
                    background: 'var(--color-bg)',
                    // Subtle shadow hints at the sticky edge.
                    boxShadow: '-2px 0 4px rgba(0,0,0,0.04)',
                  }}></th>
                )}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, rowIndex) => {
                const isSelected = selectedTxnId === row._txn_id
                const isBulkSelected = bulkEnabled && selectedIds.has(row._txn_id)
                const isKbTarget = !isSelected && kbTargetId === row._txn_id
                const rowBg = isSelected || isBulkSelected ? 'var(--color-accent-weak, rgba(76,141,255,0.16))' : 'var(--lp-input-bg, var(--color-bg))'
                return (
                  <tr
                    key={row._txn_id}
                    data-split-row={row._txn_id}
                    {...tw('row', rowIndex, { key: row._txn_id })}
                    onClick={() => {
                      // Single click: select-only when the embed page
                      // designer wires it up; otherwise legacy edit-on-click
                      // (transaction designer's standalone Live Preview tab).
                      if (onSelect) onSelect(row)
                      else openRow(row._txn_id)
                    }}
                    onDoubleClick={() => {
                      // Double click always opens the editor — both for
                      // select-aware callers (so users can still drill in)
                      // and for legacy edit-on-click callers (where it's a
                      // harmless no-op duplicate of the single click).
                      openRow(row._txn_id)
                    }}
                    style={{
                      borderBottom: '1px solid var(--color-border)',
                      background: (isSelected || isBulkSelected) ? 'var(--color-accent-weak, rgba(76,141,255,0.16))' : 'transparent',
                      // Keyboard target (F4 edit / Ctrl+Delete) — a left accent
                      // bar marks the row the shortcuts act on.
                      boxShadow: isKbTarget ? 'inset 3px 0 0 var(--color-accent)' : undefined,
                      cursor: 'pointer',
                      userSelect: onSelect ? 'none' : undefined,
                    }}
                    onMouseEnter={(e) => {
                      setActiveTxnId(row._txn_id)
                      if (!(isSelected || isBulkSelected)) e.currentTarget.style.background = 'var(--color-surface-2, var(--color-bg-secondary))'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = (isSelected || isBulkSelected) ? 'var(--color-accent-weak, rgba(76,141,255,0.16))' : 'transparent'
                    }}
                  >
                    {range.isLeading && bulkEnabled && (
                      <td
                        style={{ ...td, textAlign: 'center' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          aria-label="Select row"
                          checked={isBulkSelected}
                          onChange={() => {}}
                          onClick={(e) => handleRowCheckboxClick(e, row._txn_id)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                    )}
                    {paneCols.map(c => (
                      <td key={c.col_id || c.name}
                          data-tw-cell={columnFormKey(c)}
                          style={{
                            ...td,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden', textOverflow: 'ellipsis',
                          }}
                          title={renderTitle(row[columnFormKey(c)], c, row, cols, people)}
                      >
                        {renderCell(row[columnFormKey(c)], c, row, cols, people)}
                      </td>
                    ))}
                    {range.isTrailing && rowAdornment && (
                      <td style={{ ...td, whiteSpace: 'nowrap' }} data-tw-cell="_flags">
                        {rowAdornment(row)}
                      </td>
                    )}
                    {range.isTrailing && (
                    <td style={{
                      ...td, textAlign: 'right',
                      position: 'sticky', right: 0,
                      background: rowBg,
                      boxShadow: '-2px 0 4px rgba(0,0,0,0.04)',
                    }} onClick={(e) => e.stopPropagation()}>
                      <button className="btn btn-ghost btn-sm"
                              {...tw('action', rowEditable(row) ? 'edit' : 'view')}
                              onClick={() => openEdit(row._txn_id)}
                              title={rowEditable(row)
                                ? (editOverride?.name || 'Edit')
                                : (rowLockReason(row) || 'View only')}
                              style={{ padding: 4, marginRight: 2 }}>
                        {rowEditable(row) ? <Pencil size={14} /> : <Eye size={14} />}
                      </button>
                      <RowActionsMenu
                        actions={[
                          // Row-scoped custom actions run against THIS record —
                          // minus any the engine hid for it (visible_when).
                          ...visibleRowActions(rowActions, rowHiddenActions(row)).map((a) => ({
                            label: a.name,
                            hook: a.name,
                            icon: <Play size={14} />,
                            onClick: () => onRunRowAction?.(a.name, row._txn_id),
                          })),
                          ...(onAssign ? [{ label: 'Assign to\u2026', hook: 'assign', icon: <UserPlus size={14} />, onClick: () => onAssign(row._txn_id) }] : []),
                          ...(onAlias ? [{ label: 'Also known as', hook: 'alias', icon: <Tags size={14} />, onClick: () => onAlias(row._txn_id) }] : []),
                          ...(onProcessTrail ? [{ label: 'Process trail', hook: 'trail', icon: <Waypoints size={14} />, onClick: () => onProcessTrail(row._txn_id) }] : []),
                          ...(onHistory ? [{ label: 'Audit trail', hook: 'history', icon: <History size={14} />, onClick: () => onHistory(row._txn_id) }] : []),
                          ...(onMessages ? [{ label: 'Messages', hook: 'messages', icon: <Mail size={14} />, onClick: () => onMessages(row._txn_id) }] : []),
                          ...(canDelete && (!systemOverrides?.delete || visibleRowActions([systemOverrides.delete], rowHiddenActions(row)).length) ? [{ label: systemOverrides?.delete?.name || 'Delete', hook: 'delete', icon: <Trash2 size={14} />, onClick: () => onDelete(row._txn_id), danger: true }] : []),
                        ]}
                      />
                    </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
            {showTotals && (
              <tfoot>
                <ColumnTotalsRow
                  columns={paneCols}
                  columnKeyOf={columnFormKey}
                  columnWidthOf={wOf}
                  rows={visibleRows}
                  totals={totalsByFormKey!}
                  leadingCells={range.isLeading && bulkEnabled
                    ? <td style={{ background: 'var(--color-surface-2, var(--color-bg-secondary))' }} />
                    : undefined}
                  trailingCell={range.isTrailing
                    ? <>
                      {rowAdornment && <td style={{ background: 'var(--color-surface-2, var(--color-bg-secondary))' }} />}
                      <td style={{ background: 'var(--color-surface-2, var(--color-bg-secondary))' }} />
                    </>
                    : undefined}
                />
              </tfoot>
            )}
              </table>
              )
            }}
          </SplitScrollTable>
        )}
        {cz && czForm && (
          <HiddenTray
            title="Not in the list"
            action="Add"
            hint="Use ⚙ → Show in list to take a column out. The field stays on the form."
            items={(model.columns || []).map(w => w.column).filter(c => c && isListHidden(c) && !c.uda).map(c => ({
              key: colKeyOf(c),
              label: String(c.heading || c.name),
              action: 'Add',
              onShow: () => void cz.session.apply(
                czSetLeaf(czColPath(czForm, colKeyOf(c), 'list_hidden'), false), `Add column ${c.heading || c.name}`),
            }))}
          />
        )}
        {pagination && (
          <PaginationBar
            pagination={pagination}
            busy={busy}
            hasRows={rows.length > 0}
          />
        )}
      </div>
    </div>
  )
}


// Vertical separator used in the list header to set the primary action apart
// from the secondary button group.
const toolbarDivider: React.CSSProperties = {
  width: 1, height: 20, margin: '0 2px',
  background: 'var(--color-border)', flexShrink: 0,
}

// Width of the trailing actions column — just enough for the Edit pencil and
// the kebab menu. Kept in one place since it's referenced by both the
// <colgroup> entry and the table's total-width sum, which must stay in sync.
const ACTIONS_COL_WIDTH = 72

// Width of the leading checkbox column shown when bulk actions are wired.
const SELECT_COL_WIDTH = 40

// The bulk-action band shown above the grid once one or more rows are ticked.
const selectionBarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '8px 16px',
  borderBottom: '1px solid var(--color-border)',
  background: 'var(--color-surface-2, var(--color-bg-secondary))',
}

const th: React.CSSProperties = {
  padding: '8px 12px',
  textAlign: 'left',
  // Excel-like gridlines on all sides (the table uses borderCollapse).
  border: '1px solid var(--color-border)',
  fontWeight: 600,
  fontSize: '0.8425rem',
  color: 'var(--color-text-secondary)',
  // Render author-supplied headings in their authored case
  // (overrides global `table th { text-transform: uppercase }`).
  textTransform: 'none',
  letterSpacing: 'normal',
}

const td: React.CSSProperties = {
  padding: '8px 12px',
  color: 'var(--color-text)',
  border: '1px solid var(--color-border)',
}

// Plain-text tooltip for cells whose value is truncated by ellipsis.
function renderTitle(value: unknown, col: ColumnDef, row?: Record<string, unknown>, cols?: ColumnDef[],
                     people?: PeopleDirectory | null): string {
  const person = personLabel(people, columnFormKey(col), value)
  if (person) return person
  if (hasDisplayFormat(col)) return displayPlainText(value, col, row, cols)
  const s = formatCell(value, col)
  return s === '—' ? '' : s
}

// Rich cell rendering for presentation widgets: image columns show a
// thumbnail, document/url columns show a clickable link. A configured
// per-column display format (the designer "Display as" panel) takes over via
// the shared <DisplayCell>. Everything else falls back to plain-text formatCell.
function renderCell(value: unknown, col: ColumnDef, row?: Record<string, unknown>, cols?: ColumnDef[],
                    people?: PeopleDirectory | null): React.ReactNode {
  // A user-key column reads as the person, never as the key. Ahead of every
  // other rule: the stored value is an identifier, so no format applies to it.
  const person = personLabel(people, columnFormKey(col), value)
  if (person) return person
  if (hasDisplayFormat(col)) {
    return <DisplayCell value={value} col={col} row={row} columns={cols} />
  }
  const s = value == null ? '' : String(value).trim()
  if (s) {
    if (hasImageUpload(col)) {
      return (
        <img
          src={s}
          alt={displayNameForUrl(s)}
          style={{ height: 28, width: 28, objectFit: 'cover', borderRadius: 4, verticalAlign: 'middle' }}
        />
      )
    }
    if (hasDocumentUpload(col) || hasUrlWidget(col)) {
      const label = hasDocumentUpload(col) ? displayNameForUrl(s) : s
      return (
        <a
          href={s}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          style={{ color: 'var(--color-accent)', textDecoration: 'underline' }}
        >
          {label}
        </a>
      )
    }
  }
  return formatCell(value, col)
}

function formatCell(value: unknown, col: ColumnDef): string {
  if (value === null || value === undefined) return '—'
  const rich = richCellText(value, col)
  if (rich != null) return rich || '—'
  const when = temporalCellText(value, col)
  if (when != null) return when

  // For ddlb / radio / checkbox edit-masks the stored value is the "data"
  // code; show the user-facing "display" label instead.
  const label = ddlbDisplayLabel(value, col)
  if (label != null) return label

  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (value instanceof Date) return value.toLocaleString()
  return String(value)
}
